import { useState, useEffect, lazy, Suspense } from 'react';
import { api } from './lib/api';
import { trackEvent } from './lib/analytics';
import { AppUser } from './types';
import { Loader2 } from 'lucide-react';
import { HomeLandingPage } from './pages/HomeLandingPage';
import { AppRoutes } from './components/main/AppRoutes';
import { UiPrefsProvider } from './contexts/UiPrefsContext';
import { JobsProvider } from './contexts/JobsContext';
import { ConsentBanner } from './components/ConsentBanner';
import { ConsentModal } from './components/ConsentModal';

// The authenticated bookmark app (Sidebar, grid, modals + all its data hooks)
// is the bulk of the JS bundle but only ever renders for a logged-in user — and
// never during prerender/hydration, which always resolves to `!user` and paints
// the eager landing below. Lazy-load it so a logged-out visitor on a prerendered
// marketing page doesn't download the whole app just to see static HTML.
const AuthenticatedApp = lazy(() => import('./AuthenticatedApp'));

// True when the document arrived as prerendered (SSG) HTML — the server only
// serves the prerendered landing to logged-out visitors, so on '/' this tells
// the app to paint the landing on the first render (matching the server markup)
// instead of the auth spinner, which would otherwise trip hydration.
const HYDRATING_PRERENDER =
  typeof document !== 'undefined' &&
  !!document.getElementById('root')?.hasChildNodes();

function FullscreenSpinner() {
  return (
    <div className="h-screen w-full flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
      <Loader2 className="w-8 h-8 animate-spin text-zinc-500" />
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap auth state on mount.
  useEffect(() => {
    api.getMe().then((u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  // `signup_complete` funnel event (MF-203). The auth flows redirect a brand-new
  // account to `?signup=1` (server sets it only on provision, across all
  // providers — see withSignupFlag in routes/auth.ts). Fire once, then strip the
  // param so a refresh/back doesn't double-count. trackEvent buffers through the
  // post-consent tracker-load race, so this is safe even if umami isn't ready yet.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('signup') !== '1') return;
    trackEvent('signup_complete');
    params.delete('signup');
    const qs = params.toString();
    window.history.replaceState(
      {},
      '',
      window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash,
    );
  }, []);

  const renderMainApp = () => {
    if (loading) {
      // Logged-out '/' hydrated from prerendered landing HTML — render it
      // immediately so the first client render matches the server DOM instead
      // of swapping in a spinner (which would trip hydration).
      if (HYDRATING_PRERENDER) return <HomeLandingPage />;
      return <FullscreenSpinner />;
    }

    if (!user) return <HomeLandingPage />;

    return (
      <Suspense fallback={<FullscreenSpinner />}>
        <AuthenticatedApp user={user} />
      </Suspense>
    );
  };

  return (
    <UiPrefsProvider enabled={!!user}>
      <JobsProvider enabled={!!user}>
        <AppRoutes mainView={renderMainApp()} />
        <ConsentBanner />
        <ConsentModal />
      </JobsProvider>
    </UiPrefsProvider>
  );
}
